/**
 * Founder-pack shelf copy (founder-packs.md §3a, §4, §4a, §7; AD-661 F2 slice 11). Pure helpers
 * the store panel renders: every number is the server's (`GET /api/store/packs`) — nothing here
 * computes a price, it only orders and words what the server sent.
 */

/** The §4 sentences every pack page carries (the item descriptions carry them too). */
export const NEVER_REISSUED = 'Never reissued.';
export const NOT_TRADEABLE_AT_LAUNCH = 'Not tradeable at launch.';

export interface PackCosmetic {
  id: string;
  name: string;
  kind: string;
}

export interface PackShelfItem {
  id: string;
  tier: number;
  cosmetics: PackCosmetic[];
  gleam: number;
  /** The server's display-only real-money equivalent of `gleam` (§5 Display; AD-024). */
  gleamRealMoneyEquivalent: string;
  priceCents: number;
  currency: string;
  closesAt: string | null;
  upgrade?: {
    fromPackId: string;
    creditCents: number;
    payCents: number;
    /** `G_j − G_i` — the Gleam the upgrade credits (§3a item 1), server-computed. */
    gleam: number;
    /** The server's display-only real-money equivalent of that `gleam`. */
    gleamRealMoneyEquivalent: string;
  };
}

export interface FounderStatusView {
  founder: boolean;
  tier: number | null;
  packId: string | null;
  /** A live link whose chain is broken (§3a item 6; K6/K20): every pack checkout refuses. */
  brokenChain?: boolean;
}

/** `firstlight-spark` ⇒ `Firstlight Spark` (packs carry no display name; the id is the name). */
export function packLabel(id: string): string {
  return id
    .split(/[-_]/)
    .filter((w) => w.length > 0)
    .map((w) => w[0]!.toUpperCase() + w.slice(1))
    .join(' ');
}

/** Minor units ⇒ a display price in the pack's own currency. */
export function formatCents(cents: number, currency: string): string {
  try {
    return new Intl.NumberFormat('en-GB', {
      style: 'currency',
      currency: currency.toUpperCase(),
    }).format(cents / 100);
  } catch {
    return `${(cents / 100).toFixed(2)} ${currency.toUpperCase()}`;
  }
}

export interface PriceLine {
  label: string;
  amount: string;
}

/**
 * The price block, in the §3a item 3 order for a holder of a lower tier: full price, the credit
 * for the tier already held, the amount to pay. The credit is a statement of a prior purchase —
 * never worded as a discount, a saving or a reference price (Q-FP-14).
 */
export function packPriceLines(pack: PackShelfItem): PriceLine[] {
  const full = { label: 'Price', amount: formatCents(pack.priceCents, pack.currency) };
  if (!pack.upgrade) return [full];
  return [
    full,
    {
      label: `You already hold ${packLabel(pack.upgrade.fromPackId)}`,
      amount: formatCents(pack.upgrade.creditCents, pack.currency),
    },
    { label: 'Amount to pay', amount: formatCents(pack.upgrade.payCents, pack.currency) },
  ];
}

/**
 * The words beside the pack's included Gleam (§5 Display; the AD-024 price-display rule): the
 * server's real-money equivalent, stated as an equivalent only. Whether the pack may be
 * described as "worth" it is open (§10 Q-FP-2), so this never says worth, value or saving.
 */
export function packGleamEquivalent(pack: PackShelfItem): string {
  return `real-money equivalent ${packGleamLine(pack).equivalent}`;
}

export interface GleamLine {
  label: string;
  gleam: number;
  equivalent: string;
}

/**
 * The Gleam row of one pack as THIS caller would receive it (F2 gate r2 K18; AD-024 "fully
 * disclosed"). A holder upgrading is credited `G_j − G_i` (§3a item 1), so the row shows the
 * server's upgrade figure and its equivalent — never the pack's full `G_j` beside an amount to
 * pay that buys less. A direct buy shows the pack's own figure.
 */
export function packGleamLine(pack: PackShelfItem): GleamLine {
  return pack.upgrade
    ? {
        label: 'Gleam added by this upgrade',
        gleam: pack.upgrade.gleam,
        equivalent: pack.upgrade.gleamRealMoneyEquivalent,
      }
    : { label: 'Gleam included', gleam: pack.gleam, equivalent: pack.gleamRealMoneyEquivalent };
}

export type PackAction =
  | { kind: 'buy' }
  | { kind: 'upgrade' }
  | { kind: 'held' }
  | { kind: 'none' };

/**
 * What the shelf offers for one pack given the caller's ledger-derived status. The server
 * re-checks all of it at checkout AND at settlement; this only avoids offering a button the
 * server would refuse (`alreadyOwned` / `upgradeRequired`).
 */
export function packAction(pack: PackShelfItem, status: FounderStatusView | null): PackAction {
  if (status?.brokenChain) return { kind: 'none' };
  if (!status || status.tier === null) return { kind: 'buy' };
  if (pack.upgrade) return { kind: 'upgrade' };
  if (pack.tier <= status.tier) return { kind: 'held' };
  return { kind: 'none' };
}

/** Words for a founder status (§7 — derived from purchases, never from holdings). */
export function founderStatusLine(status: FounderStatusView | null): string | null {
  if (!status?.founder || !status.packId) return null;
  return `Founder — ${packLabel(status.packId)}`;
}

/**
 * Why the shelf offers nothing on a broken chain (§3a item 6, gate K6): a lower tier under an
 * upgrade was refunded, so no founder tier is held and every pack checkout refuses.
 */
export function founderChainNotice(status: FounderStatusView | null): string | null {
  return status?.brokenChain
    ? 'A lower founder tier under your upgrade was refunded, so that upgrade holds no tier and no pack can be bought on this account.'
    : null;
}

/** Human words for the typed pack-checkout refusals (never the raw code). */
export function describePackRefusal(code: string): string {
  switch (code) {
    case 'consentRequired':
      return 'Tick the consent box above to continue to checkout.';
    case 'packNotOnSale':
      return 'That pack is not on sale right now.';
    case 'alreadyOwned':
      return 'You already hold this pack or a higher one.';
    case 'upgradeRequired':
      return 'You hold a lower tier — use the upgrade price shown for this pack.';
    case 'nothingToUpgrade':
      return 'There is no tier to upgrade from on this account.';
    case 'checkoutUnavailable':
      return 'Checkout is not available on this server.';
    case 'unknownPack':
      return 'That pack no longer exists — reopen the store.';
    case 'priceUnresolved':
    case 'providerError':
      return 'The payment provider could not open checkout — nothing was charged. Try again later.';
    case 'emailUnverified':
      return 'Verify your email address before purchasing — check your inbox, or use Account → resend verification.';
    default:
      return `Checkout refused (${code}).`;
  }
}

/** The E3 claim step's words (§7: "claim your Firstlight items — enable 2FA"). */
export function describeClaimRefusal(code: string): string {
  if (code === 'mfaRequired')
    return 'Enable two-factor authentication (Account → Two-factor authentication) to claim these items.';
  return `Claim refused (${code}).`;
}
